'use client';

import { keepPreviousData, useQuery } from '@tanstack/react-query';

import { UnauthQueryOptions } from '@/api';
import { DesktopSideAdBanner } from '@/app/_components/ads';
import { DesktopPageShell } from '@/components/template/DesktopPageShell';

import type { DonationBusinessSort } from '../../_hooks/useIntegratedSearchFilter';
import { PAGE_SIZE } from '../../constants';
import { buildPaginationMeta } from '../../utils';
import { SearchSectionShell } from '../SearchSectionShell';
import { DonationBusinessList } from './DonationBusinessList';
import { DonationFilter } from './DonationFilter';
import { DonationSortDropdown } from './DonationSortDropdown';
import { SearchDonationSkeletonList } from './SearchDonationCardSkeleton';

interface DonationBusinessSearchProps {
  keyword: string;
  page: number;
  onPageChange: (page: number) => void;
  isFilterOpen: boolean;
  onCloseFilter: () => void;
  institutionIds: number[];
  sort: DonationBusinessSort;
  onToggleInstitution: (id: number) => void;
  onSortChange: (sort: DonationBusinessSort) => void;
  onReset: () => void;
}

export function DonationBusinessSearch({
  keyword,
  page,
  onPageChange,
  isFilterOpen,
  onCloseFilter,
  institutionIds,
  sort,
  onToggleInstitution,
  onSortChange,
  onReset,
}: DonationBusinessSearchProps) {
  const { data, isLoading } = useQuery({
    ...UnauthQueryOptions['get:/wello/v2/allows/query/donations-businesses']({
      query: {
        searchKeyword: keyword,
        institutionIds:
          institutionIds.length > 0 ? institutionIds : undefined,
        sortType: sort,
        page,
        size: PAGE_SIZE,
      },
    }),
    placeholderData: keepPreviousData,
  });

  const meta = buildPaginationMeta(data?.context?.totalCount ?? 0);

  return (
    <DesktopPageShell
      className="mt-20 w-full mobile-tablet:mt-0"
      leftAside={
        <DonationFilter
          isDrawerOpen={isFilterOpen}
          onCloseDrawer={onCloseFilter}
          onApplyDrawer={onCloseFilter}
          institutionIds={institutionIds}
          onToggleInstitution={onToggleInstitution}
          onReset={onReset}
        />
      }
      rightAside={<DesktopSideAdBanner />}
    >
      <SearchSectionShell
        meta={meta}
        page={page}
        pageSize={PAGE_SIZE}
        onPageChange={onPageChange}
        header={
          <DonationSortDropdown sort={sort} onSortChange={onSortChange} />
        }
      >
        {isLoading ? (
          <SearchDonationSkeletonList count={PAGE_SIZE} />
        ) : (
          <DonationBusinessList items={data?.context?.contents ?? []} />
        )}
      </SearchSectionShell>
    </DesktopPageShell>
  );
}
